import React, { useMemo, useState } from "react";
import { Button, Typography, message } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/light";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

const { Text } = Typography;

const MarkdownRoot = styled.div`
  color: #1f1f1f;
  font-size: 14px;
  line-height: 1.75;
  overflow-wrap: anywhere;
  white-space: normal;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 12px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 20px 0 10px;
    line-height: 1.35;
    font-weight: 600;
  }

  h1 {
    font-size: 24px;
  }

  h2 {
    font-size: 20px;
  }

  h3 {
    font-size: 17px;
  }

  ul,
  ol {
    margin: 0 0 12px;
    padding-left: 22px;
  }

  li + li {
    margin-top: 4px;
  }

  blockquote {
    margin: 0 0 12px;
    padding: 8px 12px;
    color: #595959;
    background: #fafafa;
    border-left: 3px solid #d9d9d9;
  }

  table {
    width: 100%;
    margin: 12px 0;
    border-collapse: collapse;
    table-layout: fixed;
  }

  th,
  td {
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    vertical-align: top;
  }

  th {
    background: #fafafa;
    font-weight: 600;
  }

  code {
    padding: 2px 5px;
    border-radius: 4px;
    background: #f5f5f5;
    font-size: 13px;
  }

  pre code {
    padding: 0;
    background: transparent;
  }

  img {
    max-width: 100%;
    border-radius: 6px;
  }

  input[type="checkbox"] {
    margin-right: 6px;
  }
`;

interface MarkdownRendererProps {
  content?: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content = "",
  className,
}) => {
  const [copied, setCopied] = useState("");

  const components = useMemo(
    () => ({
      code({ className: codeClassName, children, ...props }: any) {
        const match = /language-(\w+)/.exec(codeClassName || "");
        const codeText = String(children).replace(/\n$/, "");
        if (!match) {
          return (
            <code className={codeClassName} {...props}>
              {children}
            </code>
          );
        }

        return (
          <div
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              overflow: "hidden",
              margin: "12px 0",
            }}
          >
            <div
              style={{
                padding: "8px 12px",
                background: "#fafafa",
                borderBottom: "1px solid #d9d9d9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Text strong>{match[1].toUpperCase()}</Text>
              <Button
                size="small"
                type={copied === codeText ? "primary" : "text"}
                icon={copied === codeText ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(codeText);
                  setCopied(codeText);
                  message.success("代码已复制到剪贴板");
                  setTimeout(() => setCopied(""), 2000);
                }}
              >
                {copied === codeText ? "已复制" : "复制"}
              </Button>
            </div>
            <div style={{ maxHeight: 420, overflow: "auto", background: "#fff" }}>
              <SyntaxHighlighter
                language={match[1]}
                // @ts-ignore style type mismatch
                style={docco}
                customStyle={{
                  margin: 0,
                  background: "#fff",
                  padding: "10px 14px",
                }}
                {...props}
              >
                {codeText}
              </SyntaxHighlighter>
            </div>
          </div>
        );
      },
    }),
    [copied]
  );

  return (
    <MarkdownRoot className={className}>
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {content}
      </ReactMarkdown>
    </MarkdownRoot>
  );
};

export default MarkdownRenderer;
